import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import type { CSVValidationError } from "@tellescope/utils";
import { toast } from "sonner";
import medicationTemplateCSV from "./medication_template.csv?url";
import { validateMedicationCSV, transformMedicationData, MAX_MEDICATION_CSV_BYTES } from "./utils";

export type MedicationValidationResult = ReturnType<typeof validateMedicationCSV>;

export interface UploadedFile {
  file: File;
  id: string;
  validationResult?: MedicationValidationResult;
  isValidating?: boolean;
}

export interface UseMedicationUploadFormArgs {
  contact: TellescopeModelFromName<"endusers">;
  onUpload?: () => void;
}

export const useMedicationUploadForm = ({ contact, onUpload }: UseMedicationUploadFormArgs) => {
  const { t } = useTranslation("contacts");
  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);

  const uploadMedicationCSVMutation = useTellescopeMutation({
    model: "enduser_medications",
    method: "createSome",
    onSuccess: () => {
      setSelectedFiles([]);
      onUpload?.();
    },
    onError: () => {
      toast.error(t("Failed to upload medications. Please try again."));
    },
  });

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (file) => file.type === "text/csv" || file.name.endsWith(".csv")
    );

    const newFiles: UploadedFile[] = files.map((file) => ({
      file,
      id: Math.random().toString(36),
      isValidating: true,
    }));

    setSelectedFiles((prev) => [...prev, ...newFiles]);

    await Promise.all(
      newFiles.map(async (uploadedFile) => {
        try {
          if (uploadedFile.file.size > MAX_MEDICATION_CSV_BYTES) {
            throw new Error(
              t("File is larger than {{limit}}MB", { limit: Math.round(MAX_MEDICATION_CSV_BYTES / (1024 * 1024)) })
            );
          }

          const content = await uploadedFile.file.text();
          const validationResult = validateMedicationCSV(content);

          setSelectedFiles((prev) =>
            prev.map((f) => (f.id === uploadedFile.id ? { ...f, validationResult, isValidating: false } : f))
          );
        } catch (error) {
          setSelectedFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id
                ? {
                    ...f,
                    validationResult: {
                      data: [],
                      errors: [
                        {
                          row: 0,
                          field: "",
                          message: t("Failed to read file: {{reason}}", {
                            reason: error instanceof Error ? error.message : t("Unknown error"),
                          }),
                          severity: "error",
                        } satisfies CSVValidationError,
                      ],
                      warnings: [],
                      isValid: false,
                      metadata: {
                        totalRows: 0,
                        validRows: 0,
                        invalidRows: 0,
                        headers: [],
                      },
                    },
                    isValidating: false,
                  }
                : f
            )
          );
        }
      })
    );
  };

  const removeFile = (id: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearFiles = () => setSelectedFiles([]);

  // Rows that failed validation are reported per-file above and skipped on import, so the gate is
  // whether anything survived validation — not whether every file was error-free.
  const hasImportableRows =
    !selectedFiles.some((f) => f.isValidating) && selectedFiles.some((f) => (f.validationResult?.data.length ?? 0) > 0);

  const handleSubmit = async () => {
    if (!selectedFiles.length) return;

    const allValidated = selectedFiles.every((f) => !f.isValidating && f.validationResult);
    if (!allValidated) {
      return;
    }

    const allMedicationData = selectedFiles.flatMap((file) => {
      if (!file.validationResult?.data) return [];
      return transformMedicationData(file.validationResult.data, contact.id);
    });

    if (allMedicationData.length === 0) {
      return;
    }

    await uploadMedicationCSVMutation.mutateAsync(allMedicationData);
  };

  const downloadExample = () => {
    const a = document.createElement("a");
    a.href = medicationTemplateCSV;
    a.download = "medication_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return {
    selectedFiles,
    hasImportableRows,
    isImporting: uploadMedicationCSVMutation.isPending,
    handleFileSelect,
    removeFile,
    clearFiles,
    handleSubmit,
    downloadExample,
  };
};
